import * as React from 'react';
import styles from "./styles.module.css";
import { useLanguageLocale } from '../../../hooks/language-locale-hooks';
import { CURRENT_PAGE_SESSION, RECORD_PER_PAGE_LIMIT } from '../../../constants';
import { useDispatch, useSelector } from 'react-redux';
import { Selectors } from '../../../selectors';
import { UserManagementActions } from '../../../actions/actions/usermanagement-actions';
import { Actions, Thunks } from '../../../actions';
import DataTable, { Column } from '../../../components/DataTable/DataTable';
import { DeviceGroupType, getDeviceType, getFireDeviceStatusState } from '../../enum/ENUM';
import { ChevronRight, Star, StarBorder } from '@mui/icons-material';
import { IconButton, Typography } from '@mui/material';
import { pushAppRoute } from '../../../data/domain/route-manager';
import { useHistory } from 'react-router';
import { FirePanelDetailsList, FirePanelListSortFormat } from '../../../types/firepanel';

export interface Props {
    organizationId?: string;
    buildingId?: string;
    systemId?: string;
    serviceType?: number;
    ticketStatus?: number;
    searchText?: string;
    isFavorite?: boolean;
    currentPage: number;
    setCurrentPage: (current: number) => void;
}

const FirePanelListTable: React.FunctionComponent<Props> = (Props) => {

    const {
        organizationId,
        buildingId,
        searchText,
        isFavorite,
        currentPage,
        setCurrentPage
    } = Props;

    const { t } = useLanguageLocale();
    const asset = useSelector(Selectors.Asset.assets);
    const dispatch = useDispatch();
    const history = useHistory();
    const [firePanelList, setFirePanelList] = React.useState<FirePanelDetailsList>([]);
    const [isDataLoaded, setIsDataLoaded] = React.useState(true);
    const [isFirstCall, setFirstCall] = React.useState(true);
    const [totalRecords, setTotalRecords] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(RECORD_PER_PAGE_LIMIT);
    const [offset, setOffset] = React.useState(1);
    const [sorting, setSorting] = React.useState<Array<{ name: string, sort: string }>>([]);
    const [columns, setColumns] = React.useState<Column[]>([
        {
            id: 'isFavorite', label: '', hideSortIcon: true, format(value, record) {
                return record.isFavorite == 'true' ?
                    <Star data-testid='removeMarkAsFaVBuilding' onClick={() => { }} style={{ color: '#007BC1' }} /> :
                    <StarBorder data-testid='MarkAsFaVBuilding' onClick={() => { }} style={{ color: '#727E84' }} />
            },
        },
        {
            id: 'name', label: t('DEVICE_DETAILS.NAME'), hideSortIcon: true, format(value, record) {
                return record.systemName;
            },
        },
        {
            id: 'organization', label: t('DEVICE_DETAILS.ORGANIZATION'), sortColumn: 'organizationSort', sortDirection: 'asc', format(value, record) {
                return record.projectName;
            },
        },
        {
            id: 'building', label: t('DEVICE_DETAILS.BUILDING'), sortColumn: 'buildingSort', sortDirection: 'asc', format(value, record) {
                return record.buildingName;
            },
        },
        {
            id: 'type', label: t('DEVICE_DETAILS.TYPE'), sortColumn: 'systemTypeSort', sortDirection: 'asc', width: '7%', format(value, record) {
                return getDeviceType(record.type)
            },
        },
        {
            id: 'inputzones', label: t('DEVICE_DETAILS.INPUT_ZONES'), hideSortIcon: true, width: '10%', format(value, record) {
                return <div style={{ textAlign: 'right' }}>{record.inputZone}</div>;
            },
        },
        {
            id: 'devices', label: t('DEVICE_DETAILS.DEVICES'), hideSortIcon: true, width: '5%', format(value, record) {
                return <div style={{ textAlign: 'right' }}>{record.devicesCount}</div>;
            },
        },
        {
            id: 'status', label: t('DEVICE_DETAILS.STATUS'), sortColumn: 'statusSort', sortDirection: 'asc', width: '10%', format(value, record) {
                const deviceState = getFireDeviceStatusState(record.systemStatus);
                return <div
                    style={{
                        display: 'flex',
                        alignItems: 'left',
                    }}
                >
                    <div style={{ minWidth: 25 }}>
                        <img
                            src={deviceState.statusImage}
                            alt="logo"
                            style={{ display: 'flex', alignItems: 'right' }}
                            id="device-status-icon"
                            data-testid="device-status-icon"
                        /></div>
                    <Typography
                        id="device-status-button"
                        data-testid="device-status-button"
                        style={{ color: deviceState.statusColor, textTransform: 'none', marginLeft: 10, fontWeight: 600 }} variant="body2">
                        {t(deviceState.statusTitle)}
                    </Typography>
                </div>
            },
        },
        {
            id: 'action', label: '', width: '5%', format(value, row) {
                return <>
                    <IconButton edge="end" id="list-item-next-icon" onClick={() => {
                        navigateToSystemDetail(row);
                    }}>
                        <ChevronRight />
                    </IconButton>
                </>
            },
            hideSortIcon: true
        },
    ])

    const navigateToSystemDetail = (row: any) => {
        // const device = row;
        // pushAppRoute(history,
        //     { type: 'AssetDetails', deviceId: device.systemId, gatewayId: device.gateWayId, projectId: device.projectId },
        //     { backRoute: 'Assets', deviceId: device.systemId, buildingId: device.buildingId, projectId: device.projectId, gatewayId: device.gateWayId, sourcePath: 'Assets' });
    }

    const getFirePanelList = (list: FirePanelDetailsList): FirePanelDetailsList => {
        const records = list.map((data, maintenanceActivity) => {
            return (
                {
                    ...data,
                }
            );
        });

        return records;
    };

    const handlePageChange = (pageNumber: number) => {
        window.sessionStorage.setItem(CURRENT_PAGE_SESSION, pageNumber.toString());
        setOffset(pageNumber + 1);
        setCurrentPage(pageNumber);
    }

    const handleRowsPerPageChange = (rowsPerPage: number) => {
        setRowsPerPage(rowsPerPage);
    }

    const handleSort = (columnKey: string, direction: string) => {
        setColumns((prevColumns) =>
            prevColumns.map((column) =>
                column.sortColumn === columnKey
                    ? { ...column, sortDirection: column.sortDirection === 'asc' ? 'desc' : 'asc' }
                    : column
            )
        );

        setFirePanelList([...firePanelList]);

        if (columnKey && direction) {
            const index = sorting.findIndex((sort) => sort.name === columnKey);

            if (index > -1) {
                setSorting(sorting.map((sort, i) =>
                    i === index ? { ...sort, sort: direction === "asc" ? "0" : "1" } : sort
                ));
            } else {
                setSorting([
                    ...sorting,
                    {
                        name: columnKey,
                        sort: direction === "asc" ? "0" : "1"
                    }
                ])
            }
        }
    }

    React.useEffect(() => {
        dispatch(Actions.Asset.firePanelListUnmounted())
        dispatch(Thunks.Asset.getFirePanelList(String(DeviceGroupType.FireSystem), offset.toString(), rowsPerPage.toString()));
        setFirstCall(false);
    }, []);// eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        if (!isFirstCall) {
            dispatch(Thunks.Asset.getFirePanelList(String(DeviceGroupType.FireSystem), offset.toString(), rowsPerPage.toString(), sorting));
        }
    }, [organizationId, buildingId, searchText, rowsPerPage, offset, sorting]);// eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        if (asset.firePanelListLoading) {
            setIsDataLoaded(true);
        }
        else {
            if (!asset.firePanelListLoading && asset.firePanelListSuccess) {
                if (asset.firePanelList.detail.length > 0) {
                    const FirePanelList = getFirePanelList(asset.firePanelList.detail);
                    setTotalRecords(asset.firePanelList.totalRecordCount);
                    setFirePanelList(FirePanelList);
                } else {
                    setFirePanelList([]);
                }
                setIsDataLoaded(false);
            } else if (!asset.firePanelListLoading && asset.firePanelListFail) {
                setFirePanelList([]);
                setIsDataLoaded(false);
            }
        }
        dispatch(UserManagementActions.maintenanceDetailUnmounted())

    }, [asset.firePanelListLoading, asset.firePanelListSuccess, asset.firePanelListFail]); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        setOffset(currentPage + 1)
    }, [currentPage])

    return (
        <>
            <DataTable
                isLoading={isDataLoaded}
                columns={columns}
                records={firePanelList}
                count={totalRecords}
                currentPage={currentPage}
                emptyListMessage={t('ASSETS_DETAILS.NO_FIRE_PANELS_FOUND')}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                onSort={handleSort}
            />
        </>
    );
};

export default FirePanelListTable;
